import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS, fr } from 'date-fns/locale';
import { Subject, takeUntil } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { ReportService } from 'src/app/services/api/reports/report.service';
import { AuthService } from 'src/app/services/core/auth.service';

type ReportFrequency = 'daily' | 'weekly' | 'monthly' | 'custom';

type TabLabel = {
  value: ReportFrequency;
  label: string;
};

type ReportLink = {
  url: string;
  dashboard: string;
  createdDate: string;
};

type ReportData = Record<ReportFrequency, ReportLink[] | Record<string, ReportLink[]>>;

type AppUser = {
  theme: string;
  language: string;
  currentProject?: any;
  role?: { name: string };
};

@Component({
    selector: 'app-report-list-dialog',
    imports: [
        CommonModule,
        MatDialogModule,
        MatTableModule,
        MatTabsModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslateModule
    ],
    templateUrl: './report-list-dialog.component.html',
    styleUrl: './report-list-dialog.component.scss'
})

export class ReportListDialogComponent implements OnDestroy {
  // Propriétés simplifiées et typées
  project: any = null;
  user: AppUser | null = null;
  loading = true;
  isDark = false;
  
  // Données des rapports
  reportData: ReportData = {} as ReportData;
  dataSource = new MatTableDataSource<ReportLink>([]);
  displayedColumns = ['dashboard', 'createdDate'];
  
  // Gestion des tabs
  tabLabels: TabLabel[] = [{ value: 'daily', label: 'report.daily' }];
  activeTabIndex = 0;
  
  // Gestion des nested tabs (pour custom)
  nestedTabLabels: Array<{ value: string; label: string }> = [];
  activeNestedTabIndex = 0;
  showNestedTabs = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ReportListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService,
    public translate: TranslateService,
    private reportService: ReportService,
    private pop: Pop
  ) {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.loadUser();
    this.loadReportData();
  }

  private loadUser(): void {
    const user = this.authService.currentUser();
    this.user = user;
    this.isDark = user?.theme === 'dark';
    this.project = user?.currentProject;
    
    if (this.isAdmin()) {
      this.displayedColumns = [...this.displayedColumns, 'actions'];
    }
  }

  private loadReportData(): void {
    this.reportService.getReportLinks()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.processReportData(data);
        this.loading = false;
        this.selectTab(0);
      });
  }

  private processReportData(data: any): void {
    // Initialiser les labels de tabs
    this.tabLabels = [
      { value: 'daily', label: 'report.daily' },
      { value: 'weekly', label: 'report.weekly' },
      { value: 'monthly', label: 'report.monthly' },
      { value: 'custom', label: 'report.custom' }
    ];

    // Copier et nettoyer les données
    this.reportData = { ...data };
    
    // Supprimer les fréquences vides
    (Object.keys(this.reportData) as ReportFrequency[]).forEach(freq => {
      const freqData = this.reportData[freq];
      if (Array.isArray(freqData) && freqData.length === 0) {
        delete (this.reportData as any)[freq];
      }
    });

    // S'assurer qu'il y a au moins daily
    if (Object.keys(this.reportData).length === 0) {
      this.reportData.daily = [];
    }

    // Filtrer les tabs pour ne garder que ceux qui ont des données
    this.tabLabels = this.tabLabels.filter(tab => 
      (this.reportData as any)[tab.value] !== undefined
    );

    // Traiter les données custom pour les nested tabs
    this.processCustomData();
  }

  private processCustomData(): void {
    const customData = this.reportData.custom;
    if (customData && typeof customData === 'object' && !Array.isArray(customData)) {
      this.nestedTabLabels = Object.keys(customData)
        .filter(key => key !== 'length')
        .map(key => ({
          value: key,
          label: `report.custom_${key}`
        }));
    }
  }

  selectTab(index: number): void {
    this.activeTabIndex = index;
    const selectedTab = this.tabLabels[index];
    
    if (!selectedTab) return;

    const tabData = this.reportData[selectedTab.value];

    if (selectedTab.value === 'custom' && this.isCustomDataNested(tabData)) {
      this.showNestedTabs = true;
      this.selectNestedTab(0);
    } else {
      this.showNestedTabs = false;
      this.activeNestedTabIndex = 0;
      this.updateDataSource(Array.isArray(tabData) ? tabData : []);
    }
  }

  selectNestedTab(index: number): void {
    this.activeNestedTabIndex = index;
    const nestedTabValue = this.nestedTabLabels[index]?.value;
    
    if (nestedTabValue) {
      const customData = this.reportData.custom as Record<string, ReportLink[]>;
      const nestedData = customData[nestedTabValue] || [];
      this.updateDataSource(nestedData);
    }
  }

  private updateDataSource(data: ReportLink[]): void {
    this.dataSource = new MatTableDataSource(data);
  }

  private isCustomDataNested(data: any): boolean {
    return data && typeof data === 'object' && !Array.isArray(data);
  }

  getReportsCount(index: number): number {
    const selectedTab = this.tabLabels[index];
    if (!selectedTab) return 0;

    const tabData = this.reportData[selectedTab.value];
    
    if (selectedTab.value === 'custom' && this.isCustomDataNested(tabData)) {
      const customData = tabData as Record<string, ReportLink[]>;
      return Object.values(customData).reduce((total, arr) => total + (arr?.length || 0), 0);
    }
    
    return Array.isArray(tabData) ? tabData.length : 0;
  }

  formatDate(dateString: string, freq: ReportFrequency): string {
    try {
      const date = new Date(dateString);
      const timezone = this.project?.timezone || 'Europe/Paris';
      const locale = this.getDateLocale();
      
      const formatPattern = freq === 'custom' ? 'PPPp' : 'PP';
      
      return formatInTimeZone(date, timezone, formatPattern, { locale });
    } catch (error) {
      return dateString; // Fallback en cas d'erreur
    }
  }

  private getDateLocale() {
    const language = this.user?.language || 'fr-FR';
    return language.startsWith('fr') ? fr : enUS;
  }

  isAdmin(): boolean {
    return ['dev', 'admin'].includes(this.user?.role?.name || '');
  }

  delete(link: ReportLink): void {
    const confirmMessage = this.translate.instant('report.confirmDelete');
    
    this.pop.confirm(confirmMessage, (result: any) => {
      if (result.ok) {
        this.executeDelete(link);
      }
    });
  }

  private executeDelete(link: ReportLink): void {
    const blobName = encodeURIComponent(link.url.split('/report-pdf/').pop() || '');
    
    this.reportService.deleteReportLink(blobName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.removeFromDataSource(link);
          this.showSuccessMessage();
        },
        error: (error) => {
          this.handleDeleteError(error);
        }
      });
  }

  private removeFromDataSource(linkToRemove: ReportLink): void {
    const currentData = this.dataSource.data;
    const updatedData = currentData.filter(link => link.url !== linkToRemove.url);
    this.updateDataSource(updatedData);
  }

  private showSuccessMessage(): void {
    const message = this.translate.instant('http.reports.link.delete.success');
    this.pop.success(message);
  }

  private handleDeleteError(error: any): void {
    if (error.status === 403) {
      this.pop.error(this.translate.instant('http.error.403'));
    } else {
      const message = this.translate.instant('http.reports.link.delete.error', { 
        errorCode: error.status 
      });
      this.pop.error(message);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
